import { useState } from 'react';
import PropTypes from 'prop-types';
import './Table.css';

/**
 * Table Component
 * 
 * A reusable table with sorting, selection, and custom rendering.
 * 
 * @param {array} columns - Column definitions [{key, label, sortable, render}]
 * @param {array} data - Table data
 * @param {function} onSort - Sort callback (column, direction)
 * @param {string} sortColumn - Currently sorted column key
 * @param {string} sortDirection - Sort direction: 'asc' or 'desc'
 * @param {boolean} selectable - Enable row selection
 * @param {array} selectedRows - Selected row IDs
 * @param {function} onSelectRow - Row selection callback
 * @param {function} onSelectAll - Select all callback
 * @param {function} onRowClick - Row click callback
 * @param {string} emptyMessage - Message when no data
 * @param {boolean} loading - Loading state
 */
export default function Table({
  columns,
  data,
  onSort,
  sortColumn,
  sortDirection = 'asc',
  selectable = false,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  onRowClick,
  emptyMessage = 'No data available',
  loading = false
}) {
  const [hoveredRow, setHoveredRow] = useState(null);

  const handleSort = (column) => {
    if (!column.sortable || !onSort) return;
    
    const newDirection = 
      sortColumn === column.key && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(column.key, newDirection);
  };

  const handleSelectAll = (e) => {
    if (onSelectAll) {
      onSelectAll(e.target.checked);
    }
  };

  const handleSelectRow = (rowId) => {
    if (onSelectRow) {
      onSelectRow(rowId);
    }
  };

  const handleRowClick = (row) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  if (loading) {
    return (
      <div className="table-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="table-empty">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className="table">
        <thead className="table-header">
          <tr>
            {selectable && (
              <th className="table-cell table-cell-checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={handleSelectAll}
                  aria-label="Select all rows"
                />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key}
                className={`table-cell ${column.sortable ? 'table-cell-sortable' : ''}`}
                onClick={() => handleSort(column)}
              >
                <div className="table-cell-content">
                  <span>{column.label}</span>
                  {column.sortable && (
                    <span className="table-sort-icon">
                      {sortColumn === column.key ? (
                        sortDirection === 'asc' ? '↑' : '↓'
                      ) : (
                        '↕'
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table-body">
          {data.map((row, index) => (
            <tr
              key={row.id || index}
              className={`table-row ${onRowClick ? 'table-row-clickable' : ''} ${
                hoveredRow === index ? 'table-row-hovered' : ''
              }`}
              onMouseEnter={() => setHoveredRow(index)}
              onMouseLeave={() => setHoveredRow(null)}
              onClick={() => handleRowClick(row)}
            >
              {selectable && (
                <td className="table-cell table-cell-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(row.id)}
                    onChange={() => handleSelectRow(row.id)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select row ${index + 1}`}
                  />
                </td>
              )}
              {columns.map((column) => (
                <td key={column.key} className="table-cell">
                  {column.render ? column.render(row, row[column.key]) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      render: PropTypes.func
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  onSort: PropTypes.func,
  sortColumn: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  selectable: PropTypes.bool,
  selectedRows: PropTypes.array,
  onSelectRow: PropTypes.func,
  onSelectAll: PropTypes.func,
  onRowClick: PropTypes.func,
  emptyMessage: PropTypes.string,
  loading: PropTypes.bool
};
